import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import { ReactComponent as LeftArrowIcon } from '../../../assets/icons/anatomyOrientationControl/left.svg';
import { ReactComponent as RightArrowIcon } from '../../../assets/icons/anatomyOrientationControl/right.svg';
import { ReactComponent as FrontArrowIcon } from '../../../assets/icons/anatomyOrientationControl/front.svg';
import { ReactComponent as BackArrowIcon } from '../../../assets/icons/anatomyOrientationControl/back.svg';
import { ReactComponent as TopArrowIcon } from '../../../assets/icons/anatomyOrientationControl/top.svg';
import { ReactComponent as BottomArrowIcon } from '../../../assets/icons/anatomyOrientationControl/bottom.svg';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { AnatomyOrientationControlProps, IAnatomyOrientationController } from './interfaces';
import { TYPES as AnatomyTYPES } from './types';
import { useInjection } from 'inversify-react';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';

export enum AnatomicalArrowDirection {
  Left = 'left',
  Right = 'right',
  Top = 'top',
  Bottom = 'bottom',
  Back = 'back',
  Front = 'front'
}

export interface ArrowButtonProps {
  type: string;
  selected: boolean;
}

const orientationChangeSubscriptions = new DisposablesBasket();

const AnatomyOrientationControl: React.FC<AnatomyOrientationControlProps> = ({
  integrationApi,
  AnatomyIcon,
  extensionComponent
}) => {
  const controller = useInjection<IAnatomyOrientationController>(AnatomyTYPES.IAnatomyOrientationController);
  controller.setIntegrationApi(integrationApi);

  const arrowDirectionsIcons: Record<
    AnatomicalArrowDirection,
    React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  > = {
    [AnatomicalArrowDirection.Top]: TopArrowIcon,
    [AnatomicalArrowDirection.Bottom]: BottomArrowIcon,
    [AnatomicalArrowDirection.Back]: BackArrowIcon,
    [AnatomicalArrowDirection.Front]: FrontArrowIcon,
    [AnatomicalArrowDirection.Left]: LeftArrowIcon,
    [AnatomicalArrowDirection.Right]: RightArrowIcon
  };

  const [selected, setSelected] = React.useState<AnatomicalArrowDirection | undefined>(undefined);
  const isArrowSelected = (direction: AnatomicalArrowDirection): boolean => selected === direction;

  useEffect(() => {
    orientationChangeSubscriptions.add(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
      integrationApi.trackModelFrontOrientationChanges(async () => {
        const arrowDirection = await controller.checkDirectionMatch();
        setSelected(arrowDirection);
      })
    );
    orientationChangeSubscriptions.add(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
      integrationApi.trackCameraOrientationChanges(async () => {
        const arrowDirection = await controller.checkDirectionMatch();
        setSelected(arrowDirection);
      })
    );
    return (): void => {
      orientationChangeSubscriptions.dispose();
    };
  }, [controller, integrationApi]);

  const computePlacement = (direction: AnatomicalArrowDirection): TooltipProps['placement'] => {
    if (direction === AnatomicalArrowDirection.Front) {
      return 'right';
    }
    return [AnatomicalArrowDirection.Back, AnatomicalArrowDirection.Top, AnatomicalArrowDirection.Left].includes(
      direction
    )
      ? 'top'
      : 'bottom';
  };

  const ExtensionComponent = extensionComponent;

  return (
    <Container className="orientation-control">
      {Object.entries(arrowDirectionsIcons).map((arrow) => {
        const IconComponent = arrow[1];
        const direction: AnatomicalArrowDirection = arrow[0] as AnatomicalArrowDirection;
        const isSelected = isArrowSelected(direction);
        return (
          <ArrowButton key={direction} type={direction} selected={isSelected}>
            <Tooltip title={direction} placement={computePlacement(direction)}>
              <div>
                <IconComponent onClick={(): void => controller.changeDirection(direction)} />
              </div>
            </Tooltip>
          </ArrowButton>
        );
      })}
      <AnatomyIconContainer data-testid="anatomyIcon">
        <AnatomyIcon />
      </AnatomyIconContainer>
      {ExtensionComponent && <ExtensionComponent integrationApi={integrationApi} />}
    </Container>
  );
};

const ArrowButton: React.FC<PropsWithChildren<ArrowButtonProps>> = ({ type, selected, children }) => {
  return (
    <IconContainer data-testid={type + 'ArrowBtn'} type={type} selected={selected}>
      {children}
    </IconContainer>
  );
};

const IconContainer = styled.div<{ type: string; selected: boolean }>`
  position: absolute;
  cursor: pointer;
  z-index: 1;
  background-color: transparent;
  opacity: 0.8;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.5));
  border-radius: 0.75em;
  fill: #fafafa;
  cursor: pointer;
  transition: all 0.1s;
  pointer-events: auto;

  ${(props): string => {
    switch (props.type) {
      case AnatomicalArrowDirection.Left:
        return `right: 1.2em;
                top: 2.4em;
                width: 2.4em;`;
      case AnatomicalArrowDirection.Right:
        return `left: 1.5em;
                top: 5em;
                width: 2.5em;`;
      case AnatomicalArrowDirection.Front:
        return `right: 1em;
                top: 4.9em;
                width: 2.5em;`;
      case AnatomicalArrowDirection.Back:
        return `left: 1.5em;
                top: 2.3em;
                width: 2.5em;`;
      case AnatomicalArrowDirection.Top:
        return `left: 4em;
                top: 0.8em;
                width: 2.5em;`;
      case AnatomicalArrowDirection.Bottom:
        return `left: 4em;
                bottom: 1.5em;
                width: 2.5em;`;
      default:
        return '';
    }
  }};

  path,
  polygon {
    stroke: none;
    opacity: 0.7;
  }
  :hover path,
  :hover polygon {
    fill: ${({ theme }): string => theme.palette.primary.main};
    opacity: 1;
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.5));
    transform: scale(1.05);
  }
  path {
    fill: ${({ selected, theme }): string =>
      selected ? theme.palette.primary.light : theme.palette.primary.contrastText};
    opacity: 1;
  }

  polygon {
    fill: ${({ selected, theme }): string =>
      selected ? theme.palette.primary.light : theme.customPalette.panels.arrowsOutline};
    opacity: 1;
  }
`;

const AnatomyIconContainer = styled.div`
  position: absolute;
  width: 3.2em;
  left: 3.6em;
  top: 3.4em;
  background-color: transparent;
  opacity: 0.8;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.5));
  fill: #fafafa;
  pointer-events: none;
  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const Container = styled.div`
  width: 10em;
  height: 11em;
  position: absolute;
  z-index: 1;
  right: 0.4em;
  overflow: hidden;
  bottom: 0;
  pointer-events: none;
`;

export default AnatomyOrientationControl;

